import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PencilLine } from 'lucide-react';
import { useAccess } from '../lib/access';
import { artUrl } from '../api/client';
import { useDominantColor } from '../lib/color';
import { Cover } from './Cover';
import { hashHue } from '../lib/format';

interface Props {
  kind: string;
  title: string;
  art?: string;
  round?: boolean;
  meta?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** Éditeur de cette page (affiché aux personnes qui ont le droit d'envoi) */
  editPath?: string;
  /** Actions placées après « Modifier » (suppression pour les administrateurs…) */
  trailing?: ReactNode;
}

/** En-tête de page détaillée, teinté par la couleur dominante de l'image */
export function PageHeader({ kind, title, art, round, meta, actions, children, editPath, trailing }: Props) {
  const { canUpload } = useAccess();
  const edit = canUpload && editPath;
  // Sans image exploitable, le lavis reprend la teinte du motif généré pour ce nom
  const wash = useDominantColor(art ? artUrl(art, 128) : undefined, `hsl(${hashHue(title)} 34% 26%)`);
  const long = title.length > 34;
  return (
    <header className="hero" style={{ ['--wash' as string]: wash }}>
      <div className="hero__inner">
        <Cover art={art} name={title} size={512} round={round} className="hero__art" eager />
        <div className="hero__text">
          <span className="hero__kind">{kind}</span>
          <h1 className={`hero__title ${long ? 'hero__title--long' : ''}`}>{title}</h1>
          {meta && <div className="hero__meta">{meta}</div>}
          {children}
        </div>
      </div>
      {(actions || edit || trailing) && (
        <div className="hero__actions">
          {actions}
          {edit && (
            <Link className="btn btn--ghost" to={editPath} title="Corriger les informations, la pochette…">
              <PencilLine size={16} /> Modifier
            </Link>
          )}
          {trailing}
        </div>
      )}
    </header>
  );
}

export function PlayButton({ onClick, label = 'Lecture', big }: { onClick: () => void; label?: string; big?: boolean }) {
  return (
    <button className={`btn-play ${big ? 'btn-play--big' : ''}`} onClick={onClick} aria-label={label}>
      <svg viewBox="0 0 24 24" width={big ? 26 : 20} height={big ? 26 : 20} aria-hidden>
        <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" fill="currentColor" />
      </svg>
    </button>
  );
}
